/*
 * Live events: one EventSource per org, written straight into the
 * TanStack Query caches. Events are the fast path, never the only one:
 * a reconnect re-reads what was missed (every query, and every operation
 * someone is waiting on), work that runs is also polled every few seconds
 * while it runs, and a chain (restore point, then the work) moves on by
 * reading the restore point's `followUp`, so a dropped or silent stream
 * can delay a step by a few seconds but never freeze it.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { api } from "./client";
import { applyBulkEvent } from "./bulk";
import { keys, type OpFilter } from "./keys";
import {
  isActiveOp,
  type LogLine,
  type MetricPoint,
  type Operation,
  type OperationWithLogs,
  type ServerDetail,
  type ServerSummary,
  type SiteDetail,
  type SiteSummary,
} from "./types";

/* ── Connection state, readable from any component ──────────────── */

export type LiveState = "connecting" | "open" | "down";
let liveState: LiveState = "connecting";
const liveListeners = new Set<() => void>();
function setLiveState(s: LiveState) {
  if (s === liveState) return;
  liveState = s;
  liveListeners.forEach((l) => l());
}
export function useLiveState(): LiveState {
  return useSyncExternalStore(
    (cb) => {
      liveListeners.add(cb);
      return () => liveListeners.delete(cb);
    },
    () => liveState,
  );
}

/* ── Log buffer ─────────────────────────────────────────────────────
 * Log lines can arrive before the operation's GET returns (or for an
 * operation nobody has opened yet). Keep recent lines per operation so a
 * later fetch can merge them in and nothing is lost in the gap. */

const MAX_BUFFERED_OPS = 40;
const MAX_LINES = 5000;
const logBuffer = new Map<string, LogLine[]>();

function bufferLog(opId: string, line: LogLine) {
  let lines = logBuffer.get(opId);
  if (!lines) {
    if (logBuffer.size >= MAX_BUFFERED_OPS) {
      const oldest = logBuffer.keys().next().value;
      if (oldest) logBuffer.delete(oldest);
    }
    lines = [];
    logBuffer.set(opId, lines);
  }
  lines.push(line);
  if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
}

export function mergeLogs(base: LogLine[], extra: LogLine[]): LogLine[] {
  if (!extra.length) return base;
  const seen = new Set(base.map((l) => l.seq));
  const add = extra.filter((l) => !seen.has(l.seq));
  if (!add.length) return base;
  return [...base, ...add].sort((a, b) => a.seq - b.seq);
}

export function bufferedLogs(opId: string): LogLine[] {
  return logBuffer.get(opId) ?? [];
}

/* ── Cache writers ──────────────────────────────────────────────── */

function upsertById<T extends { id: string }>(list: T[], item: T, prepend = false): T[] {
  const i = list.findIndex((x) => x.id === item.id);
  if (i === -1) return prepend ? [item, ...list] : [...list, item];
  const next = list.slice();
  next[i] = item;
  return next;
}

/** Kinds the operation lists leave out unless they failed (docs/api.md, `routine`). */
export const ROUTINE_KINDS = new Set(["site.health", "server.audit", "site.inventory", "server.facts"]);
export const isRoutineQuiet = (op: Pick<Operation, "kind" | "status">) => ROUTINE_KINDS.has(op.kind) && op.status !== "failed" && op.status !== "expired";

function opMatches(f: OpFilter, op: Operation) {
  if (f.serverId && f.serverId !== op.serverId) return false;
  if (f.siteId && f.siteId !== op.siteId) return false;
  if (op.quiet) return false;
  if (!f.routine && isRoutineQuiet(op)) return false;
  return true;
}

/**
 * Write an event into one cached query. If that query is fetching right now,
 * its response may have been read before the event happened and would
 * overwrite it, so refetch it too (this cancels the in-flight request).
 */
function patch<T>(qc: QueryClient, key: QueryKey, fn: (old: T) => T) {
  const st = qc.getQueryState<T>(key);
  if (!st) return;
  if (st.data !== undefined) qc.setQueryData<T>(key, (old) => (old === undefined ? old : fn(old)));
  if (st.fetchStatus === "fetching") void qc.invalidateQueries({ queryKey: key, exact: true });
}

function refetch(qc: QueryClient, key: QueryKey) {
  void qc.invalidateQueries({ queryKey: key, exact: true });
}

function applyOperation(qc: QueryClient, orgId: string, op: Operation) {
  patch<OperationWithLogs>(qc, keys.operation(orgId, op.id), (old) => ({ ...old, operation: op }));

  for (const q of qc.getQueryCache().findAll({ queryKey: keys.operationLists(orgId) })) {
    const f = (q.queryKey[3] ?? {}) as OpFilter;
    if (!opMatches(f, op)) continue;
    const data = q.state.data as { operations: Operation[] } | undefined;
    if (!data || q.state.fetchStatus === "fetching" || !data.operations.some((x) => x.id === op.id)) {
      // New to this list, or mid-fetch: refetch rather than guess.
      refetch(qc, q.queryKey);
      continue;
    }
    qc.setQueryData(q.queryKey, { operations: upsertById(data.operations, op) });
  }

  if (!isActiveOp(op.status)) {
    // A finished operation can change things the summary events don't
    // carry (a site's environments, a server's facts), so refetch those.
    // Not exact: a site's repo and deploy history live under its key.
    if (op.siteId) void qc.invalidateQueries({ queryKey: keys.site(orgId, op.siteId) });
    refetch(qc, keys.server(orgId, op.serverId));
    void qc.invalidateQueries({ queryKey: keys.sites(orgId) });
    void qc.invalidateQueries({ queryKey: keys.servers(orgId) });
    void qc.invalidateQueries({ queryKey: ["org", orgId, "activity"] });
  }
}

function applyLog(qc: QueryClient, orgId: string, e: { operationId: string } & LogLine) {
  const line: LogLine = { seq: e.seq, at: e.at, stream: e.stream, line: e.line };
  // Buffered lines are merged into the operation's next fetch, so a log
  // line never needs a refetch of its own.
  bufferLog(e.operationId, line);
  qc.setQueryData<OperationWithLogs>(keys.operation(orgId, e.operationId), (old) =>
    old ? { ...old, logs: mergeLogs(old.logs, [line]) } : old,
  );
}

function applyServer(qc: QueryClient, orgId: string, s: ServerSummary) {
  const listKey = keys.servers(orgId);
  const list = qc.getQueryData<{ servers: ServerSummary[] }>(listKey);
  // A removed server reports status "deleted" (not in the summary type).
  if ((s.status as string) === "deleted") {
    if (list) qc.setQueryData(listKey, { servers: list.servers.filter((x) => x.id !== s.id) });
    qc.removeQueries({ queryKey: keys.server(orgId, s.id), exact: true });
    return;
  }
  // Events also arrive for an agency's client orgs, so only update servers
  // this list already shows; anything new means the list should refetch.
  if (list && !list.servers.some((x) => x.id === s.id)) refetch(qc, listKey);
  else patch<{ servers: ServerSummary[] }>(qc, listKey, (old) => ({ servers: upsertById(old.servers, s) }));

  // The event carries only the storage status and a security summary; keep the detail's full cards.
  const { backupStore: store, security: sec, mail, ...summary } = s;
  patch<{ server: ServerDetail }>(qc, keys.server(orgId, s.id), (old) => ({
    server: {
      ...old.server,
      ...summary,
      backupStore: old.server.backupStore && store ? { ...old.server.backupStore, status: store.status } : old.server.backupStore,
    },
  }));
  // Storage appeared or went away, or the security picture changed: the cards need their full details.
  const detail = qc.getQueryData<{ server: ServerDetail }>(keys.server(orgId, s.id));
  if (
    detail &&
    (!!detail.server.backupStore !== !!store ||
      (mail?.status ?? null) !== (detail.server.mail?.status ?? null) ||
      (sec && detail.server.security && (sec.rebootRequired !== detail.server.security.rebootRequired || sec.securityPackages !== (detail.server.security.packages?.security ?? null))))
  )
    refetch(qc, keys.server(orgId, s.id));

  // Extend the 1-hour chart with the newest heartbeat.
  const m = s.metrics;
  if (m) {
    qc.setQueryData<{ points: MetricPoint[] }>(keys.metrics(orgId, s.id, "1h"), (old) => {
      if (!old) return old;
      const last = old.points[old.points.length - 1];
      if (last && Date.parse(last.at) >= Date.parse(m.at)) return old;
      const cutoff = Date.parse(m.at) - 60 * 60 * 1000;
      const points = [...old.points, { ...m, load1: null }].filter((p) => Date.parse(p.at) >= cutoff);
      return { points };
    });
  }
}

/**
 * A `site` event over the list's row: the event wins, but the list's own card fields (Live's `canWpSignIn` and
 * `toDeploy`, which events don't carry) stay until the list is read again.
 */
function mergeSummary(old: SiteSummary | undefined, next: SiteSummary): SiteSummary {
  if (!old?.live || !next.live || old.live.id !== next.live.id) return { ...old, ...next };
  const live = { ...next.live };
  if (live.canWpSignIn === undefined) live.canWpSignIn = old.live.canWpSignIn;
  if (live.toDeploy === undefined) live.toDeploy = old.live.toDeploy;
  return { ...old, ...next, live };
}

function applySite(qc: QueryClient, orgId: string, s: SiteSummary & { environments?: SiteDetail["environments"] }) {
  const { environments: envs, ...summary } = s;
  // The org's own list, and the Sites page's list with its clients' sites (under the same key); other fields kept.
  for (const q of qc.getQueryCache().findAll({ queryKey: keys.sites(orgId) })) {
    const listKey = q.queryKey;
    const list = qc.getQueryData<{ sites: SiteSummary[] }>(listKey);
    // Not a list of sites under the same key (New site's placement, ["org", id, "sites", "placement"]): left alone.
    if (list && !Array.isArray(list.sites)) continue;
    if (s.deleted) {
      if (list) qc.setQueryData(listKey, { ...list, sites: list.sites.filter((x) => x.id !== s.id) });
    } else if (list && !list.sites.some((x) => x.id === s.id)) {
      refetch(qc, listKey);
    } else {
      patch<{ sites: SiteSummary[] }>(qc, listKey, (old) => ({ ...old, sites: upsertById(old.sites, mergeSummary(old.sites.find((x) => x.id === s.id), summary)) }));
    }
  }
  patch<{ site: SiteDetail }>(qc, keys.site(orgId, s.id), (old) => {
    const environments = envs ?? (s.live ? upsertById(old.site.environments, s.live) : old.site.environments);
    return { site: { ...old.site, ...summary, environments } };
  });
}

/* ── Watching operations the user started ───────────────────────── */

/** What to say when a watched operation succeeds, instead of "<title>: Finished." */
export type WatchMessage = { title: string; description?: string };
const watched = new Map<string, WatchMessage | null>();
/** When each watched operation was last heard of (an event or a read), so the fallback only asks about quiet ones. */
const lastHeard = new Map<string, number>();
const startListeners = new Set<(opId: string) => void>();
/** The org an operation belongs to: the one on screen when it started, or a client's (its site acted on from the agency's Sites list). */
export type WatchHome = { id: string; slug: string };
const watchedHome = new Map<string, WatchHome>();
/** Where a watched operation lives. */
export const watchedHomeOf = (id: string) => watchedHome.get(id) ?? null;
/** The org on screen (useLiveEvents): work started without a home of its own is that org's. */
let openOrg: WatchHome | null = null;

/**
 * Report this operation's outcome to the user when it finishes. `home`: its org, read from there (it may be a
 * client's); the org on screen when left out, so it's still followed, and said, after the person opens another org.
 */
export function watchOperation(id: string, success?: WatchMessage, home?: WatchHome) {
  const at = home ?? openOrg;
  if (at) watchedHome.set(id, at);
  const isNew = !watched.has(id);
  // Keep a message set earlier (a restore point's) rather than dropping it.
  watched.set(id, success ?? watched.get(id) ?? null);
  lastHeard.set(id, Date.now());
  if (isNew) startListeners.forEach((l) => l(id));
}
/** Someone started work the panel follows (the task tray says "Started" and shows its progress). */
export function onWatchStart(fn: (opId: string) => void) {
  startListeners.add(fn);
  return () => {
    startListeners.delete(fn);
  };
}

/**
 * What a watched operation's change means: still going, handed on to the work it was protecting (a restore point
 * whose work was dispatched: watch that instead, with the same message), or finished, with the outcome to report.
 * A restore point whose work was canceled is reported as the work, not as a successful backup.
 */
function followWatched(op: Operation): { report: Operation; success: WatchMessage | null; home: WatchHome | null } | null {
  if (!watched.has(op.id)) return null;
  lastHeard.set(op.id, Date.now());
  const f = op.followUp;
  if (f) {
    if (f.status === "dispatched" && f.operationId) {
      const msg = watched.get(op.id) ?? null;
      const home = watchedHome.get(op.id);
      watched.delete(op.id);
      lastHeard.delete(op.id);
      watchedHome.delete(op.id);
      if (home) watchedHome.set(f.operationId, home);
      if (!watched.has(f.operationId)) watched.set(f.operationId, msg);
      lastHeard.set(f.operationId, 0); // ask about it at the next fallback read
      return null;
    }
    if (f.status === "cancelled" && !isActiveOp(op.status)) {
      const home = watchedHome.get(op.id) ?? null;
      watched.delete(op.id);
      lastHeard.delete(op.id);
      watchedHome.delete(op.id);
      return { report: { ...op, status: "failed", title: f.label, error: { code: "cancelled", message: f.error ?? op.error?.message ?? "The restore point failed, so nothing was changed." } }, success: null, home };
    }
    return null; // the restore point runs, or it's done and its work is about to be dispatched
  }
  if (isActiveOp(op.status)) return null;
  const success = watched.get(op.id) ?? null;
  const home = watchedHome.get(op.id) ?? null;
  watched.delete(op.id);
  lastHeard.delete(op.id);
  watchedHome.delete(op.id);
  return { report: op, success, home };
}

/** Other modules (restore point chains) can follow operation events too. */
const opListeners = new Set<(op: Operation) => void>();
export function onOperationEvent(fn: (op: Operation) => void) {
  opListeners.add(fn);
  return () => opListeners.delete(fn);
}

/* ── The bell (api/notifications.ts listens) ─────────────────────── */

/** A `notification` event: a new or updated row, or rows read in another tab, and the person's counts now. */
export type NotificationEvent = { notification?: unknown; read?: string[] | "all"; unread: number; urgentUnread: number };
const notificationListeners = new Set<(ev: NotificationEvent) => void>();
export function onNotificationEvent(fn: (ev: NotificationEvent) => void) {
  notificationListeners.add(fn);
  return () => {
    notificationListeners.delete(fn);
  };
}

/* ── The hook ───────────────────────────────────────────────────── */

export function useLiveEvents(orgId: string, onWatchedFinished?: (op: Operation, success: WatchMessage | null, home: WatchHome | null) => void, orgSlug?: string) {
  const qc = useQueryClient();
  const onFinished = useRef(onWatchedFinished);
  useEffect(() => {
    onFinished.current = onWatchedFinished;
  });
  useEffect(() => {
    openOrg = orgId && orgSlug ? { id: orgId, slug: orgSlug } : null;
  }, [orgId, orgSlug]);

  useEffect(() => {
    if (!orgId) return;
    let es: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let opened = false;
    let disposed = false;

    // One path for an operation, whether it came as an event, from a re-read after a reconnect, or from the fallback poll.
    const handleOp = (op: Operation) => {
      // Another org's work (a client's site) isn't in this org's lists; it's only followed to its end.
      const home = watchedHome.get(op.id);
      if (!home || home.id === orgId) {
        applyOperation(qc, orgId, op);
        opListeners.forEach((l) => l(op));
      }
      const done = followWatched(op);
      if (done) onFinished.current?.(done.report, done.success, done.home && done.home.id !== orgId ? done.home : null);
    };
    // Read the watched operations again: after a reconnect all of them, otherwise the ones nothing was heard of lately.
    const readWatched = async (quietFor = 0) => {
      const now = Date.now();
      for (const id of [...watched.keys()]) {
        if (now - (lastHeard.get(id) ?? 0) < quietFor) continue;
        try {
          const res = await api.get<OperationWithLogs>(`/orgs/${watchedHome.get(id)?.id ?? orgId}/operations/${id}`);
          if (!disposed) handleOp(res.operation);
        } catch {
          // Gone or not ours any more: stop asking.
          watched.delete(id);
          lastHeard.delete(id);
          watchedHome.delete(id);
        }
      }
    };
    // The fallback: anything watched and quiet for 4 seconds is read, so a silent stream can't freeze it.
    const poll = setInterval(() => {
      if (watched.size && !document.hidden) void readWatched(4_000);
    }, 4_000);
    // A stream can stay "open" and say nothing (a proxy holding a dead connection). Once the API sends visible
    // `ping` events, a minute and more of silence means it's gone: reconnect.
    let pingsSeen = false;
    let lastEventAt = Date.now();
    const heard = () => {
      lastEventAt = Date.now();
    };
    const watchdog = setInterval(() => {
      if (pingsSeen && es && Date.now() - lastEventAt > 70_000) {
        es.close();
        es = null;
        setLiveState("down");
        connect();
      }
    }, 15_000);

    const parse = <T,>(e: MessageEvent): T | null => {
      try {
        return JSON.parse(e.data) as T;
      } catch {
        return null;
      }
    };

    const connect = () => {
      setLiveState("connecting");
      es = new EventSource(`/api/orgs/${orgId}/events`, { withCredentials: true });

      es.onopen = () => {
        setLiveState("open");
        heard();
        // After a reconnect, read everything again: what's on screen, and every operation someone is waiting on.
        if (opened) {
          void qc.invalidateQueries({ queryKey: keys.org(orgId) });
          void readWatched();
        }
        opened = true;
        attempt = 0;
      };
      es.addEventListener("ping", () => {
        pingsSeen = true;
        heard();
      });

      es.onerror = () => {
        if (!es || disposed) return;
        setLiveState("down");
        if (es.readyState === EventSource.CLOSED) {
          // The browser gave up (for example a 401 or the API is down).
          es.close();
          attempt += 1;
          timer = setTimeout(connect, Math.min(2000 * 2 ** (attempt - 1), 15_000));
        }
      };

      es.addEventListener("operation", (e) => {
        heard();
        const op = parse<Operation>(e as MessageEvent);
        if (op) handleOp(op);
      });
      es.addEventListener("operation.log", (e) => {
        heard();
        const l = parse<{ operationId: string } & LogLine>(e as MessageEvent);
        if (l) {
          applyLog(qc, orgId, l);
          if (watched.has(l.operationId)) lastHeard.set(l.operationId, Date.now());
        }
      });
      es.addEventListener("server", (e) => {
        heard();
        const s = parse<ServerSummary>(e as MessageEvent);
        if (s) applyServer(qc, orgId, s);
      });
      es.addEventListener("site", (e) => {
        heard();
        const s = parse<SiteSummary>(e as MessageEvent);
        if (s) applySite(qc, orgId, s);
      });
      // A bulk run over sites: its new state, for the tray's grouped task.
      es.addEventListener("bulk", (e) => {
        heard();
        const b = parse<Record<string, unknown>>(e as MessageEvent);
        if (b) applyBulkEvent(qc, orgId, b);
      });
      // The person's own operation or server move changed (sent to them whichever org is open): one in another org
      // is in their work elsewhere (api/work.ts), whose events this stream doesn't carry, so the tray reads it again.
      es.addEventListener("work", (e) => {
        heard();
        const w = parse<{ orgId: string }>(e as MessageEvent);
        if (w && w.orgId !== orgId) void qc.invalidateQueries({ queryKey: keys.myWork });
      });
      // Sent to the person whichever org they're looking at.
      es.addEventListener("notification", (e) => {
        const ev = parse<NotificationEvent>(e as MessageEvent);
        if (ev) notificationListeners.forEach((l) => l(ev));
      });
    };

    // A page kept in the back/forward cache would otherwise hold its stream open, and on HTTP/1.1
    // a few of those use up the browser's six connections to the API, so new requests just wait.
    const onHide = () => {
      clearTimeout(timer);
      es?.close();
      es = null;
    };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted && !disposed && !es) connect();
    };
    // Back online, or back on the tab while the stream is down: try now rather than at the next retry.
    const retryNow = () => {
      if (disposed || document.hidden || liveState === "open") return;
      clearTimeout(timer);
      es?.close();
      es = null;
      connect();
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onShow);
    window.addEventListener("online", retryNow);
    document.addEventListener("visibilitychange", retryNow);

    connect();
    return () => {
      disposed = true;
      clearInterval(poll);
      clearInterval(watchdog);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("online", retryNow);
      document.removeEventListener("visibilitychange", retryNow);
      clearTimeout(timer);
      es?.close();
      setLiveState("connecting");
    };
  }, [orgId, qc]);
}
